import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import TargetText from "../components/TargetText";
import RecordButton from "../components/RecordButton";
import FeedbackDisplay from "../components/FeedbackDisplay";
import ProFeedbackDisplay from "../components/ProFeedbackDisplay";
import ProToggle from "../components/ProToggle";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";
import UserProfileIcon from "../components/UserProfileIcon";
import Tutorial from "../components/Tutorial";
import FeatureGate from "../components/subscription/FeatureGate";
import Logo from "../components/Logo";
import RecordingLimit from "../components/subscription/RecordingLimit";
import SilenceDetectionModal from "../components/SilenceDetectionModal";
import RacingGame from "../components/RacingGame";
import ThemeToggle from "../components/ThemeToggle";
import useAudioRecorder from "../hooks/useAudioRecorder";
import useTutorial from "../hooks/useTutorial";
import useSounds from "../hooks/useSounds";
import { useAuth } from "../context/AuthContext";
import { useUserStats } from "../context/UserStatsContext";
import { useSubscription } from "../context/SubscriptionContext";
import { transcribeAudio, transcribeAudioPro, ProAnalysis } from "../services/api";
import { getMotivationalQuote } from "../services/quotesService";
import { TranscriptionResult } from "../types";
import { TETRIS_CREDIT_REWARD } from "../types/subscription";
import { homeTutorialSteps, HOME_TUTORIAL_KEY } from "../config/tutorialSteps";

const DEFAULT_SENTENCE = "The quick brown fox jumps over the lazy dog";

interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
}

type LoadingStepStatus = 'pending' | 'active' | 'completed';

interface LoadingStep {
  label: string;
  status: LoadingStepStatus;
}

const initialLoadingSteps: LoadingStep[] = [
  { label: 'Uploading audio', status: 'pending' },
  { label: 'Transcribing speech', status: 'pending' },
  { label: 'Analyzing pronunciation', status: 'pending' },
];

const HomePage: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [transcriptionResult, setTranscriptionResult] = useState<TranscriptionResult | null>(null);
  const [proAnalysis, setProAnalysis] = useState<ProAnalysis | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isPro, setIsPro] = useState<boolean>(false);
  const [targetText, setTargetText] = useState<string>(DEFAULT_SENTENCE);
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>(initialLoadingSteps);
  const [showCompletion, setShowCompletion] = useState<boolean>(false);
  const [fadeOut, setFadeOut] = useState<boolean>(false);
  const [currentPart, setCurrentPart] = useState<number>(0);
  const [textParts, setTextParts] = useState<string[]>([DEFAULT_SENTENCE]);
  const [showSilenceModal, setShowSilenceModal] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [hasReachedLimit, setHasReachedLimit] = useState<boolean>(false);
  const [showRacingModal, setShowRacingModal] = useState<boolean>(false);
  const processedBlobRef = useRef<Blob | null>(null);
  const recordedInProModeRef = useRef<boolean>(false);
  const quoteLoadedRef = useRef<boolean>(false);

  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { startSession, endSession } = useUserStats();
  const { plan, canAccess, checkAndIncrementRecording, initializeSubscription, getRecordingsRemaining, canEarnBonusCredits, addBonusCredits } = useSubscription();
  const { showTutorial, completeTutorial, skipTutorial } = useTutorial({
    storageKey: HOME_TUTORIAL_KEY,
  });
  const { playSound } = useSounds();

  // Initialize subscription for new users
  useEffect(() => {
    if (currentUser) {
      initializeSubscription().catch(console.error);
    }
  }, [currentUser, initializeSubscription]);

  // Load motivational quote on mount
  useEffect(() => {
    if (!quoteLoadedRef.current) {
      quoteLoadedRef.current = true;
      getMotivationalQuote().then((q) => {
        setTargetText(q.text);
        setTextParts([q.text]);
      }).catch(console.error);
    }
  }, []);

  const {
    isRecording,
    audioBlob,
    error: recordingError,
    recordingTime,
    startRecording: startRecordingBase,
    stopRecording,
    resetRecording,
    isPlaying,
    playRecording,
    stopPlayback,
  } = useAudioRecorder();

  // Helper to update loading step status
  const updateStep = (stepIndex: number, status: LoadingStepStatus) => {
    setLoadingSteps(prev => prev.map((step, i) => ({
      ...step,
      status: i === stepIndex ? status : (i < stepIndex ? 'completed' : step.status)
    })));
  };

  // Check if user has reached daily limit on mount and subscription changes
  useEffect(() => {
    if (plan === 'free') {
      const remaining = getRecordingsRemaining();
      setHasReachedLimit(remaining === 0);
    } else {
      setHasReachedLimit(false);
    }
  }, [plan, getRecordingsRemaining]);

  // Wrap startRecording to track session and Pro mode
  const startRecording = async () => {
    // Check recording limit for free users
    if (plan === 'free') {
      setIsValidating(true);
      try {
        const { allowed, remaining } = await checkAndIncrementRecording();
        if (!allowed) {
          // Limit reached - update state but don't block (button should be disabled)
          setHasReachedLimit(true);
          setIsValidating(false);
          return;
        }
        // Track if this was the last recording
        if (remaining === 0) {
          setHasReachedLimit(true);
        }
      } finally {
        setIsValidating(false);
      }
    }

    // Only allow Pro mode if user has access
    const useProMode = isPro && canAccess('phoneme_analysis');
    recordedInProModeRef.current = useProMode;
    startSession('pronunciation');
    await startRecordingBase();
  };

  // Process audio when recording is complete
  useEffect(() => {
    const processAudio = async (): Promise<void> => {
      // Only process if we have a new blob that hasn't been processed yet
      if (audioBlob && audioBlob !== processedBlobRef.current) {
        processedBlobRef.current = audioBlob;
        setIsProcessing(true);
        setApiError(null);
        setTranscriptionResult(null);
        setProAnalysis(null);
        setShowCompletion(false);
        setFadeOut(false);

        const useProMode = recordedInProModeRef.current;

        // Set loading steps based on mode
        if (useProMode) {
          setLoadingSteps([
            { label: 'Preparing your recording', status: 'pending' },
            { label: 'Expert teachers listening...', status: 'pending' },
            { label: 'Crafting personalized feedback', status: 'pending' },
          ]);
        } else {
          setLoadingSteps(initialLoadingSteps);
        }

        try {
          // Step 1: Uploading/Preparing audio
          updateStep(0, 'active');
          await new Promise(resolve => setTimeout(resolve, 300)); // Brief delay for visual feedback
          updateStep(0, 'completed');

          // Step 2: Transcribing speech
          updateStep(1, 'active');

          let result: TranscriptionResult;
          let proResult: ProAnalysis | null = null;

          if (useProMode) {
            // Use OpenAI Whisper for Pro mode
            const proTranscriptionResult = await transcribeAudioPro(audioBlob, targetText);
            result = proTranscriptionResult;
            proResult = proTranscriptionResult.proAnalysis;
          } else {
            // Use AssemblyAI for standard mode
            result = await transcribeAudio(audioBlob, targetText);
          }

          updateStep(1, 'completed');

          // Step 3: Analyzing pronunciation
          updateStep(2, 'active');
          await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for visual feedback

          // Calculate score for tracking
          let score: number;
          if (proResult) {
            score = proResult.overallScore;
          } else {
            const targetWords = targetText.split(/\s+/).filter(w => w.length > 0);
            const transcribedWords = result.words.map(w => w.text.toLowerCase());
            const correctCount = targetWords.filter(word =>
              transcribedWords.includes(word.toLowerCase().replace(/[.,!?;:]/g, ''))
            ).length;
            score = Math.round((correctCount / targetWords.length) * 100);
          }
          await endSession(score);

          updateStep(2, 'completed');

          // Show completion state for 1 second before showing results
          setShowCompletion(true);
          playSound('success');
          await new Promise(resolve => setTimeout(resolve, 800));

          // Start fade out
          setFadeOut(true);
          await new Promise(resolve => setTimeout(resolve, 300));

          // Show results
          setTranscriptionResult(result);
          setProAnalysis(proResult);
          setIsProcessing(false);
          setShowCompletion(false);
          setFadeOut(false);
        } catch (err) {
          console.error("Transcription error:", err);
          const apiErr = err as ApiError;
          setApiError(
            apiErr.response?.data?.error ||
              "Failed to process your recording. Please try again."
          );
          playSound('error');
          await endSession();
          setIsProcessing(false);
        }
      }
    };

    processAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob, targetText]);

  const handleTryAgain = (): void => {
    setTranscriptionResult(null);
    setProAnalysis(null);
    setApiError(null);
    processedBlobRef.current = null;
    resetRecording();
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Callback for when text parts are calculated
  const handlePartsCalculated = useCallback((parts: string[]) => {
    setTextParts(parts);
  }, []);

  // Callback to clear results when text changes
  const handleClearResults = useCallback(() => {
    setTranscriptionResult(null);
    setProAnalysis(null);
    setApiError(null);
    processedBlobRef.current = null;
    resetRecording();
  }, [resetRecording]);

  // Check for silence detection error to show modal instead
  const SILENCE_ERROR = "I can't hear you";
  const isSilenceError = recordingError?.includes(SILENCE_ERROR);

  // Show modal when silence is detected
  useEffect(() => {
    if (isSilenceError) {
      setShowSilenceModal(true);
    }
  }, [isSilenceError]);

  // Filter out silence error from regular error display
  const error = isSilenceError ? null : (recordingError || apiError);

  const handleSilenceModalClose = () => {
    setShowSilenceModal(false);
    resetRecording();
  };

  const handleSilenceModalTryAgain = () => {
    setShowSilenceModal(false);
    resetRecording();
    // Small delay to allow state to reset
    setTimeout(() => {
      startRecording();
    }, 100);
  };

  // Racing game modal handlers
  const handleRacingComplete = async (score: number) => {
    if (score >= 500) {
      const success = await addBonusCredits(TETRIS_CREDIT_REWARD);
      if (success) {
        setApiError(null);
        setHasReachedLimit(false);
      }
    }
    setShowRacingModal(false);
  };

  const handleRacingClose = () => {
    setShowRacingModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* User bar */}
      <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Logo size="md" onClick={() => navigate("/app")} />
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1 p-2 -m-2"
              title="Logout"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 sm:h-4 sm:w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span className="hidden sm:inline">Logout</span>
            </button>
            <ThemeToggle />
            <div id="user-profile">
              <UserProfileIcon />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <Logo size="xl" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Improve your English pronunciation with AI-powered feedback
          </p>
        </header>

        {/* Pro Toggle - Only show if user has Pro features */}
        <div id="pro-toggle">
          <FeatureGate feature="phoneme_analysis" showUpgradePrompt={true}>
            <ProToggle isPro={isPro} onToggle={() => setIsPro(!isPro)} />
          </FeatureGate>
        </div>

        {/* Recording Limit for Free Users */}
        <RecordingLimit className="mb-6" />

        {/* Main content */}
        <main>
          {/* Target sentence */}
          <div id="target-text">
            <TargetText
              text={targetText}
              onTextChange={setTargetText}
              disabled={isRecording || isProcessing}
              currentPart={currentPart}
              totalParts={textParts.length}
              onPartChange={setCurrentPart}
              onPartsCalculated={handlePartsCalculated}
              onClearResults={handleClearResults}
              limitReached={hasReachedLimit}
            />
          </div>

          {/* Error message (not for limit reached - that's shown after results) */}
          {error && !hasReachedLimit && (
            <div className="mb-6">
              <ErrorState
                title="Recording Failed"
                message={error}
                icon="error"
                showHomeButton={false}
                showRetryButton={true}
                onRetry={handleTryAgain}
                actions={[
                  {
                    label: 'Try Interview Practice',
                    onClick: () => navigate('/interview'),
                    variant: 'secondary',
                  },
                ]}
              />
            </div>
          )}

          {/* Recording controls */}
          <div className="flex justify-center mb-8">
            {isProcessing ? (
              <div className={`transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
                <LoadingState
                  title={showCompletion ? "Analysis complete!" : "Analyzing your pronunciation"}
                  message={showCompletion ? "Preparing your results..." : "Please wait while we process your recording..."}
                  steps={loadingSteps}
                  variant={showCompletion ? "dots" : "spinner"}
                />
              </div>
            ) : !error && !hasReachedLimit && (
              <div id="record-button">
                <RecordButton
                  isRecording={isRecording}
                  isProcessing={isProcessing}
                  isValidating={isValidating}
                  recordingTime={recordingTime}
                  onStart={startRecording}
                  onStop={stopRecording}
                  disabled={false}
                />
              </div>
            )}
          </div>

          {/* Results */}
          {transcriptionResult && (
            <>
              {isPro ? (
                <ProFeedbackDisplay
                  targetText={targetText}
                  transcriptionResult={transcriptionResult}
                  proAnalysis={proAnalysis || undefined}
                  isPlaying={isPlaying}
                  onPlayRecording={playRecording}
                  onStopPlayback={stopPlayback}
                  hasRecording={!!audioBlob}
                />
              ) : (
                <FeedbackDisplay
                  targetText={targetText}
                  transcriptionResult={transcriptionResult}
                  isPlaying={isPlaying}
                  onPlayRecording={playRecording}
                  onStopPlayback={stopPlayback}
                  hasRecording={!!audioBlob}
                />
              )}

              {/* Daily Limit Reached - shown after results */}
              {hasReachedLimit && (
                <div className="mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 text-center shadow-lg">
                  <div className="text-4xl mb-3">🎯</div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                    You've reached your daily limit
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                    Upgrade to continue practicing!
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <button
                      onClick={() => navigate('/pricing')}
                      className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl transition-all text-sm"
                    >
                      Upgrade Now
                    </button>
                    {canEarnBonusCredits() && (
                      <button
                        onClick={() => setShowRacingModal(true)}
                        className="px-5 py-2.5 bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 font-semibold rounded-xl border-2 border-indigo-200 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors text-sm flex items-center justify-center gap-2"
                      >
                        🏎️ Play Racing
                      </button>
                    )}
                  </div>
                  {canEarnBonusCredits() && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Play Racing to earn {TETRIS_CREDIT_REWARD} bonus credits!
                    </p>
                  )}
                </div>
              )}

              {/* Try Again button - only show if not at limit */}
              {!hasReachedLimit && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleTryAgain}
                    className="px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </>
          )}

          {/* Daily Limit Reached - shown when no results and at limit */}
          {!transcriptionResult && !isProcessing && !isRecording && hasReachedLimit && (
            <div className="mb-6 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                Daily Limit Reached
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                You've used all your free recordings for today. Upgrade to continue practicing!
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl transition-all"
                >
                  Upgrade Now
                </button>
                {canEarnBonusCredits() && (
                  <button
                    onClick={() => setShowRacingModal(true)}
                    className="px-6 py-3 bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 font-semibold rounded-xl border-2 border-indigo-200 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors flex items-center justify-center gap-2"
                  >
                    🏎️ Play Racing
                  </button>
                )}
              </div>
              {canEarnBonusCredits() && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Play Racing to earn {TETRIS_CREDIT_REWARD} bonus credits!
                </p>
              )}
              {!canEarnBonusCredits() && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  You've already earned the maximum bonus credits for today.
                </p>
              )}
            </div>
          )}

          {/* Instructions */}
          {!transcriptionResult && !isProcessing && !isRecording && (
            <div className="mt-8 p-6 bg-blue-50 dark:bg-gray-800 rounded-lg">
              <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                How it works:
              </h3>
              <ol className="list-decimal list-inside text-blue-700 dark:text-blue-400 space-y-1">
                <li>Click "Start Recording" and allow microphone access</li>
                <li>Read the sentence above clearly</li>
                <li>Click "Stop Recording" when finished</li>
                <li>Review your pronunciation feedback</li>
              </ol>
              {isPro && (
                <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-700">
                  <p className="text-purple-700 dark:text-purple-300 text-sm">
                    <span className="font-semibold">Pro Mode Enabled:</span>{" "}
                    You'll see detailed phoneme analysis, timing breakdown, and
                    personalized pronunciation tips.
                  </p>
                </div>
              )}
            </div>
          )}

          </main>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-400 dark:text-gray-500">
          <p className="mb-2">Powered by AI Speech Recognition</p>
          <div className="flex justify-center gap-4">
            <Link to="/pricing" className="hover:text-gray-600 dark:hover:text-gray-300">Pricing</Link>
            <Link to="/contact" className="hover:text-gray-600 dark:hover:text-gray-300">Contact</Link>
          </div>
        </footer>
      </div>

      {/* Tutorial */}
      {showTutorial && (
        <Tutorial
          steps={homeTutorialSteps}
          onComplete={completeTutorial}
          onSkip={skipTutorial}
        />
      )}

      {/* Silence Detection Modal */}
      <SilenceDetectionModal
        isOpen={showSilenceModal}
        onClose={handleSilenceModalClose}
        onTryAgain={handleSilenceModalTryAgain}
      />

      {/* Racing Game Modal */}
      <RacingGame
        isOpen={showRacingModal}
        onClose={handleRacingClose}
        onComplete={handleRacingComplete}
      />
    </div>
  );
};

export default HomePage;
